/**
 * Date helpers. The community is in Johannesburg (SAST, UTC+2) but we always
 * format in the viewer's local zone and store UTC ISO strings.
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO/UTC string -> value for <input type="datetime-local"> in LOCAL time. */
export function toDatetimeLocal(iso: string | Date): string {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value (local time) -> UTC ISO string for storage. */
export function fromDatetimeLocal(value: string): string {
    return new Date(value).toISOString();
}

/** ISO -> "YYYY-MM-DD" in local time for <input type="date">. */
export function toDateInput(iso: string | Date): string {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatDate(iso: string, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
    return new Date(iso).toLocaleDateString('en-ZA', opts);
}

export function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

/** "3 hours ago", "2 days ago", "just now". */
export function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
    const y = Math.floor(mo / 12);
    return `${y} year${y === 1 ? '' : 's'} ago`;
}

export function hoursSince(iso: string): number {
    return Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
}

export function greetingForNow(): string {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

/** Build an .ics calendar file for an event and return a download URL. */
export function buildIcs(ev: { title: string; description?: string | null; location?: string | null; start: string; durationMinutes?: number }): string {
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const start = new Date(ev.start);
    const end = new Date(start.getTime() + (ev.durationMinutes ?? 90) * 60_000);
    const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
    const lines = [
        'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Forest Town Portal//EN', 'BEGIN:VEVENT',
        `UID:${crypto.randomUUID()}@foresttown`, `DTSTAMP:${fmt(new Date())}`,
        `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`, `SUMMARY:${esc(ev.title)}`,
        ev.location ? `LOCATION:${esc(ev.location)}` : '',
        ev.description ? `DESCRIPTION:${esc(ev.description)}` : '',
        'END:VEVENT', 'END:VCALENDAR',
    ].filter(Boolean);
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    return URL.createObjectURL(blob);
}

// ---------------------------------------------------------------------------
// Move-in year
// ---------------------------------------------------------------------------

/** Nobody moved into Forest Town before this. Mirrors the Worker's own bound. */
export const EARLIEST_MOVE_IN_YEAR = 1950;

/**
 * The years a resident may pick as their move-in year, newest first.
 *
 * Signup used to ask for a full date through `<input type="date">`. On Android
 * that opens a calendar on the current month, so a resident who moved in
 * decades ago had to tap through the year header, a year list, a month and a
 * day to answer a question the app only ever renders as a year. A single
 * select is one tap and one scroll, and nothing downstream is poorer for it:
 * the tenure tiers all start on whole years.
 */
export function moveInYears(now: Date = new Date()): number[] {
    const years: number[] = [];
    for (let y = now.getFullYear(); y >= EARLIEST_MOVE_IN_YEAR; y--) years.push(y);
    return years;
}

/** The year select's value -> what `resident_since` stores. */
export const yearToMoveInDate = (year: number | string): string => `${year}-01-01`;

/** `resident_since` -> the year to preselect. Empty when the row has no date. */
export const moveInYearOf = (iso: string | null | undefined): string =>
    (iso && /^\d{4}/.test(iso) ? iso.slice(0, 4) : '');
